import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { Provider } from 'react-redux'
import { legacy_createStore as createStore } from 'redux'
import { I18nextProvider } from 'react-i18next'
import i18n from 'i18next'
import WordDetail, { PanelSentence } from '../../containers/word-detail'
import { WordPanelData } from '../../services/translationsReview'

const noop = () => undefined

/**
 * The redux state the real word panel reads, filled with one item: what get-clicked-word would
 * send, with the reviewer's text as the learner-language meaning. Nothing else in the app sees it.
 */
export const panelState = (word: WordPanelData, text: string | null, lang: string) => ({
  story: {
    wordDetailLoader: false,
    wordDetails: {
      word: word.word,
      pinyin: word.pinyin,
      english: word.english,
      test_level: word.test_level,
      localizedMeaning: text && text.trim() ? text : null,
      meaningLanguage: text && text.trim() ? lang : null,
      mastery: 'New',
    },
    currentStory: null,
    storyDictionary: {},
    audio_pronunciation_setting: 'none',
  },
  user: { credentials: { customFields: { show_english_meanings: false } }, membership: {} },
  pocket: { words: [], pending: [], loaded: true },
  dashboard: {},
})

interface Props {
  word: WordPanelData
  lang: string
  /** The text being reviewed: what the panel shows as the meaning. */
  text: string | null
  sentence?: { chinese: string; english: string | null; translation: string | null } | null
}

/**
 * The real word panel (containers/word-detail) as a learner of `lang` sees it, from fixtures: its
 * own store, its own i18next language, rendered in place instead of sliding in, and inert (no
 * audio, no lookups, no pocket or mastery writes for the reviewer's account).
 */
const WordPanelPreview: React.FC<Props> = ({ word, lang, text, sentence }) => {
  const store = useMemo(() => {
    const state = panelState(word, text, lang)
    return createStore(() => state)
  }, [word, text, lang])
  const [translator] = useState(() => i18n.cloneInstance())
  useEffect(() => {
    if (translator.language !== lang) translator.changeLanguage(lang).catch(noop)
  }, [translator, lang])

  const panelSentence: PanelSentence | null = sentence
    ? {
        index: 0,
        text: sentence.chinese,
        en: sentence.english || '',
        translation: sentence.translation
          ? { text: sentence.translation, lang, localized: true }
          : { text: sentence.english || '', lang: 'en', localized: false },
        refId: null,
      }
    : null

  return (
    <div className='td-panel' aria-label='Word panel preview'>
      <I18nextProvider i18n={translator}>
        <Provider store={store}>
          <Suspense fallback={<p className='td-muted'>Loading the word panel</p>}>
            <WordDetail
              visible={false}
              showMasterySection={false}
              sentence={panelSentence}
              showEnglish
              setOpenExampleModal={noop}
              isMasteryOpen={false}
              setMasteryOpen={noop}
              isImportanceOpen={false}
              setImportanceOpen={noop}
              setWordDetailVisible={noop}
              setActiveIndex={noop}
              openExampleModal={false}
            />
          </Suspense>
        </Provider>
      </I18nextProvider>
    </div>
  )
}

export default WordPanelPreview
